import { VStack } from '@chakra-ui/react';
import type { Dispatch, RefObject } from 'react';
import type {
  SelectOption,
  EditorProps,
  EmailApiHandle,
} from '@kula-ai/components';
import { EmailBlockSection } from '../../../components/EmailBlockSection';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import type {
  InterviewCommunicationAction,
  InterviewCommunicationFormState,
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../../../InterviewActivity/communication/types';
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/validate';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/copy';
import {
  EmailSentBadge,
  type EmailSentBadgeProps,
} from '../../editMode/EmailSentBadge';

interface InterviewerPaneProps {
  state: InterviewCommunicationFormState;
  dispatch: Dispatch<InterviewCommunicationAction>;
  fieldErrors: InterviewCommunicationValidationErrors;
  /** Bumped per submit click — surfaces each Email's errors on submit. */
  submitAttempt?: number;
  /** Imperative handle of the calendar-invite Email — `validate()` calls
   *  `focusFirstError()` on it to scroll to the bad field/variable. */
  calendarApiRef?: RefObject<EmailApiHandle | null>;
  /** Imperative handle of the email Email. */
  emailApiRef?: RefObject<EmailApiHandle | null>;
  /** Calendar-invite template options — `interviewer_notice`. */
  calendarTemplateOptions: SelectOption[];
  /** Email template options — `interviewer_interview_confirmation`. */
  emailTemplateOptions: SelectOption[];
  fromOptions: SelectOption[];
  /** Spinner state for the From Select while the delegatable-senders
   *  query resolves. */
  isFromOptionsLoading?: boolean;
  ccBccOptions: SelectOption[];
  ccBccSelectProps?: Record<string, unknown>;
  fromSelectProps?: Record<string, unknown>;
  calendarVariables: InterviewVariablesByCategory;
  emailVariables: InterviewVariablesByCategory;
  uploadInlineImage?: EditorProps['uploadInlineImage'];
  sentEmailBadge?: EmailSentBadgeProps;
  calendarPreviewProps?: Pick<
    EditorProps,
    'previewHtml' | 'onPreviewClick' | 'onPreviewDismiss'
  >;
  emailPreviewProps?: Pick<
    EditorProps,
    'previewHtml' | 'onPreviewClick' | 'onPreviewDismiss'
  >;
}

export const InterviewerPane = ({
  state,
  dispatch,
  fieldErrors,
  submitAttempt,
  calendarApiRef,
  emailApiRef,
  calendarTemplateOptions,
  emailTemplateOptions,
  fromOptions,
  isFromOptionsLoading = false,
  ccBccOptions,
  ccBccSelectProps,
  fromSelectProps,
  calendarVariables,
  emailVariables,
  uploadInlineImage,
  sentEmailBadge,
  calendarPreviewProps,
  emailPreviewProps,
}: InterviewerPaneProps) => {
  const copy = SCHEDULE_INTERVIEW_COPY.communication;

  return (
    <VStack align="stretch" spacing="4" flex="1" minW={0}>
      <EmailBlockSection
        title={copy.interviewer.calendarTitle}
        sectionId="schedule-comm-interviewer-calendar"
      >
        <TemplatedEmail
          className="w-full"
          showFrom={false}
          showTo={false}
          showSubject={false}
          enableCcBcc={false}
          submitAttempt={submitAttempt}
          apiRef={calendarApiRef}
          showTemplate
          showEditor
          editorVariant="calendarInvite"
          variables={calendarVariables}
          templateOptions={calendarTemplateOptions}
          applyTemplateBodyToEditor
          value={state.interviewerCalendar}
          onChange={(value) =>
            dispatch({ type: 'SET_INTERVIEWER_CALENDAR', payload: value })
          }
          errors={fieldErrors.interviewerCalendar}
          placeholders={{ template: copy.placeholders.template }}
          uploadInlineImage={uploadInlineImage}
          editorProps={calendarPreviewProps}
        />
      </EmailBlockSection>

      <EmailBlockSection
        title={copy.interviewer.emailTitle}
        sectionId="schedule-comm-interviewer-email"
        showSwitch
        switchChecked={state.interviewerEmailEnabled}
        onSwitchChange={(checked) =>
          dispatch({ type: 'SET_INTERVIEWER_EMAIL_ENABLED', payload: checked })
        }
        suffix={
          sentEmailBadge ? <EmailSentBadge {...sentEmailBadge} /> : undefined
        }
      >
        {state.interviewerEmailEnabled ? (
          <TemplatedEmail
            className="w-full"
            showFrom
            showTo={false}
            showSubject
            enableCcBcc
            showTemplate
            submitAttempt={submitAttempt}
            apiRef={emailApiRef}
            showEditor
            // ".ics" checkbox now in the editor toolbar (kula `Email`
            // `showIcsCheckbox`); state rides on `value.ics`, mirrored to
            // `interviewerIcsEnabled`.
            showIcsCheckbox
            editorVariant="richEmail"
            variables={emailVariables}
            attachments={state.interviewerEmailAttachments}
            onAttachmentsChange={(items: InterviewEditorAttachmentItem[]) =>
              dispatch({
                type: 'SET_INTERVIEWER_EMAIL_ATTACHMENTS',
                payload: items,
              })
            }
            templateOptions={emailTemplateOptions}
            fromOptions={fromOptions}
            fromSelectProps={{
              ...fromSelectProps,
              loading: isFromOptionsLoading,
            }}
            ccBccOptions={ccBccOptions}
            ccSelectProps={ccBccSelectProps}
            bccSelectProps={ccBccSelectProps}
            applyTemplateBodyToEditor
            value={{
              ...state.interviewerEmail,
              ics: state.interviewerIcsEnabled,
            }}
            onChange={(value) => {
              dispatch({ type: 'SET_INTERVIEWER_EMAIL', payload: value });
              if (value.ics !== state.interviewerIcsEnabled) {
                dispatch({
                  type: 'SET_INTERVIEWER_ICS_ENABLED',
                  payload: value.ics,
                });
              }
            }}
            errors={fieldErrors.interviewerEmail}
            placeholders={{
              from: copy.placeholders.from,
              subject: copy.placeholders.subject,
              template: copy.placeholders.template,
            }}
            uploadInlineImage={uploadInlineImage}
            editorProps={emailPreviewProps}
          />
        ) : null}
      </EmailBlockSection>
    </VStack>
  );
};
